import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../utils/errorText';
import { trapTab } from '../../utils/focusTrap';
import { reportTab } from '../../utils/issueReports';
import { can } from '../../utils/permissions';
import { focusElement } from '../../utils/useFocusOnOpen';
import { displayName } from '../../utils/userLabel';
import PhotoViewer from '../../components/PhotoViewer';
import { box, input } from '../shared/styles';

/*
 * Issue reports (routes/issue_reports.py): what people reported from the
 * Report issue button, for those who administer accounts (issue:manage --
 * collection managers and system admins). A deep link from anyone else is
 * told so and asks the server nothing, as /activity does.
 *
 * Tabs by status with their counts, a search over the words, the page and
 * the reporter, and one report at a time in a drawer (?open=<id>, the link
 * the administrators' email carries): the comment, where and on what it was
 * sent, its screenshots, and the triage -- status and notes.
 */
export const TABS = ['open', 'in_progress', 'resolved', 'dismissed', 'all'];
const STATUSES = ['open', 'in_progress', 'resolved', 'dismissed'];
const TONE = { open: 'danger', in_progress: 'warning', resolved: 'success', dismissed: 'neutral' };
const PAGE = 50;

const when = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(lang === 'he' ? 'he-IL' : 'en-GB',
      { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return iso;
  }
};

function StatusChip({ status, t }) {
  const tone = TONE[status] || 'neutral';
  return (
    <span data-testid="issue-status" data-status={status}
      style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 12, whiteSpace: 'nowrap',
        background: `var(--c-${tone}-soft, var(--c-bg-muted))`, color: `var(--c-${tone}-fg, var(--c-text-fg))` }}>
      {t(`status.${status}`)}
    </span>
  );
}

function ReportDrawer({ reportId, onClose, onSaved, t, lang, readOnly = false }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('open');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [viewer, setViewer] = useState(null);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  // While a screenshot is open, its viewer owns Escape and Tab.
  const viewerRef = useRef(viewer);
  viewerRef.current = viewer;

  useEffect(() => {
    let live = true;
    setReport(null); setError(''); setSaved(false);
    api.get(`/issue-reports/${reportId}`)
      .then(({ data }) => {
        if (!live) return;
        setReport(data); setStatus(data.status); setNotes(data.admin_notes || '');
      })
      .catch((e) => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [reportId]);

  useEffect(() => {
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    focusElement(headingRef.current);
    const onKey = (e) => {
      if (e.key === 'Escape' && viewerRef.current === null) { closeRef.current(); return; }
      if (viewerRef.current === null) trapTab(dialogRef.current, e, { extra: reportTab() });
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function' && opener.isConnected !== false) opener.focus({ preventScroll: true });
    };
  }, []);

  const dirty = report && (status !== report.status || (notes.trim() || null) !== (report.admin_notes || null));

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const { data } = await api.put(`/issue-reports/${reportId}`, { status, admin_notes: notes.trim() || null });
      setReport((r) => ({ ...r, ...data }));
      setSaved(true);
      onSaved?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const shots = report?.screenshots || [];
  const reporter = report ? displayName(report.reporter_name || report.reporter_username, t) : '';
  const facts = report ? [
    ['page', report.page_path ? <Link to={report.page_path} onClick={onClose}>{report.page_path}</Link> : '—'],
    ['pageTitle', report.page_title],
    ['version', report.app_version],
    ['environment', report.environment],
    ['viewAs', report.view_as_role],
    ['browser', report.user_agent],
    ['viewport', report.viewport],
    ['sentAt', when(report.created_at, lang)],
    ['updated', report.updated_at ? `${when(report.updated_at, lang)} · ${displayName(report.updated_by_name || '', t)}` : null],
  ].filter(([, v]) => v) : [];

  return (
    <div className="hub-drawer-backdrop" data-testid="issue-drawer-backdrop">
      <aside ref={dialogRef} className="hub-drawer hub-drawer--wide" role="dialog" aria-modal="true"
        aria-labelledby="issue-drawer-title" data-testid="issue-drawer">
        <div className="hub-drawer__head" style={{ marginBottom: 12 }}>
          <h3 id="issue-drawer-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
            {report ? t('drawer.title', { name: reporter }) : t('drawer.loading')}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('close')} title={t('close')} data-testid="issue-drawer-close"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--c-text-muted-fg)',
              lineHeight: 1, minWidth: 44, minHeight: 44 }}>
            ×
          </button>
        </div>
        {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
        {report && (
          <>
            <p style={{ whiteSpace: 'pre-wrap', fontSize: 15, background: 'var(--c-bg-subtle)', padding: 12, borderRadius: 6,
              overflowWrap: 'anywhere' }} data-testid="issue-comment">
              {report.comment}
            </p>
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', fontSize: 13, margin: '12px 0' }}>
              {facts.map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt style={{ color: 'var(--c-text-muted-fg)' }}>{t(`drawer.${key}`)}</dt>
                  <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{value}</dd>
                </div>
              ))}
            </dl>
            {shots.length > 0 && (
              <section style={{ margin: '12px 0' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: 14 }}>{t('drawer.screenshots', { n: shots.length })}</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {shots.map((s, i) => (
                    <button key={s.screenshot_id} type="button" onClick={() => setViewer(i)}
                      aria-label={t('drawer.openScreenshot', { n: i + 1 })} title={t('drawer.openScreenshot', { n: i + 1 })}
                      data-testid="issue-screenshot"
                      style={{ padding: 0, border: '1px solid var(--c-border)', borderRadius: 6, background: 'none',
                        cursor: 'zoom-in' }}>
                      <img src={s.url} alt="" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6,
                        display: 'block' }} />
                    </button>
                  ))}
                </div>
              </section>
            )}
            <section style={{ ...box, marginTop: 12 }}>
              <label htmlFor="issue-status" style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                {t('drawer.status')}
              </label>
              <select id="issue-status" value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...input, minHeight: 40 }}
                data-testid="issue-status-select" disabled={readOnly}>
                {STATUSES.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
              </select>
              <label htmlFor="issue-notes" style={{ display: 'block', fontWeight: 600, fontSize: 14, margin: '12px 0 4px' }}>
                {t('drawer.notes')}
              </label>
              <textarea id="issue-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} maxLength={5000}
                data-testid="issue-notes" readOnly={readOnly}
                style={{ ...input, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              {!readOnly && <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <button type="button" onClick={save} disabled={!dirty || saving} data-testid="issue-save"
                  style={{ background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
                    padding: '8px 16px', fontSize: 14, cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                    opacity: dirty && !saving ? 1 : 0.6, minHeight: 40 }}>
                  {saving ? t('drawer.saving') : t('drawer.save')}
                </button>
                {saved && !dirty && <span role="status" style={{ fontSize: 13, color: 'var(--c-success-fg)' }}>{t('drawer.saved')}</span>}
              </div>}
            </section>
          </>
        )}
      </aside>
      {viewer !== null && (
        <PhotoViewer photos={shots.map((s) => ({ s3_url: s.url, photo_id: s.screenshot_id }))} index={viewer}
          onIndex={setViewer} onClose={() => setViewer(null)} dialogLabel={t('drawer.screenshotsLabel')}
          testId="issue-screenshot-viewer" />
      )}
    </div>
  );
}

export default function IssueReports() {
  const { t, i18n } = useTranslation('issues');
  const { user } = useAuth();
  // issue:read reads the reports; issue:manage -- the same people, and not
  // the read-only executive -- triages them.
  const allowed = can(user, 'issue:read');
  const mayTriage = can(user, 'issue:manage');
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('status')) ? params.get('status') : 'open';
  const openId = params.get('open');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  // The search box waits for a pause in typing.
  useEffect(() => {
    const timer = setTimeout(() => { setQ(query.trim()); setOffset(0); }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: body } = await api.get('/issue-reports', {
        params: { status: tab, limit: PAGE, offset, ...(q ? { q } : {}) },
      });
      setData(body);
    } catch (e) {
      setError(errorText(e));
    }
  }, [tab, offset, q]);
  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  const setParam = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v === null || v === undefined || v === '') next.delete(k); else next.set(k, v); });
    setParams(next, { replace: false });
  };

  if (!allowed) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="issues-restricted">{t('accessRestricted')}</p>;
  }

  const counts = data?.counts || {};
  const countOf = (name) => (name === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[name] || 0);
  const items = data?.items || [];
  const lang = i18n?.language;

  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('pageIntro')}</p>
      <div role="tablist" aria-label={t('tabsLabel')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {TABS.map((name) => (
          <button key={name} type="button" role="tab" aria-selected={tab === name} data-testid={`issues-tab-${name}`}
            onClick={() => { setOffset(0); setParam({ status: name === 'open' ? null : name }); }}
            style={{ border: '1px solid var(--c-border)', borderRadius: 999, padding: '6px 14px', fontSize: 14, cursor: 'pointer',
              minHeight: 40, background: tab === name ? 'var(--c-primary)' : 'var(--c-surface)',
              color: tab === name ? 'var(--c-on-accent)' : 'var(--c-text-fg)' }}>
            {t(`tab.${name}`)} <span aria-hidden="true">({countOf(name)})</span>
          </button>
        ))}
      </div>
      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('search')}
        aria-label={t('search')} data-testid="issues-search"
        style={{ ...input, width: '100%', maxWidth: 420, boxSizing: 'border-box', minHeight: 40, marginBottom: 12 }} />
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {data && items.length === 0 && <p data-testid="issues-empty" style={{ color: 'var(--c-text-muted-fg)' }}>{t('empty')}</p>}
      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }} data-testid="issues-table">
            <thead>
              <tr style={{ textAlign: 'start', borderBottom: '1px solid var(--c-border)' }}>
                {['when', 'reporter', 'page', 'comment', 'screenshots', 'environment', 'status'].map((c) => (
                  <th key={c} style={{ textAlign: 'start', padding: '6px 8px', whiteSpace: 'nowrap' }}>{t(`col.${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.report_id} data-testid="issues-row" style={{ borderBottom: '1px solid var(--c-border)', cursor: 'pointer' }}
                  onClick={() => setParam({ open: r.report_id })}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{when(r.created_at, lang)}</td>
                  <td style={{ padding: '6px 8px' }}>{displayName(r.reporter_name || r.reporter_username, t)}</td>
                  <td style={{ padding: '6px 8px', overflowWrap: 'anywhere', maxWidth: 220 }}>{r.page_path || '—'}</td>
                  <td style={{ padding: '6px 8px', maxWidth: 360 }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setParam({ open: r.report_id }); }}
                      style={{ background: 'none', border: 'none', padding: 0, textAlign: 'start', cursor: 'pointer',
                        color: 'var(--c-info-fg)', font: 'inherit' }}
                      title={t('tip.openReport')}>
                      {r.comment.length > 120 ? `${r.comment.slice(0, 120)}…` : r.comment}
                    </button>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>{r.screenshot_count || ''}</td>
                  <td style={{ padding: '6px 8px' }}>{r.environment || ''}</td>
                  <td style={{ padding: '6px 8px' }}><StatusChip status={r.status} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > PAGE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>{t('prev')}</button>
          <span style={{ fontSize: 13 }}>{t('pageOf', { from: offset + 1, to: Math.min(offset + PAGE, data.total), total: data.total })}</span>
          <button type="button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>{t('next')}</button>
        </div>
      )}
      {openId && (
        <ReportDrawer reportId={openId} t={t} lang={lang} onClose={() => setParam({ open: null })} onSaved={load}
          readOnly={!mayTriage} />
      )}
    </div>
  );
}
